import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import trackingService, {
  CsxTerminalMapping,
  CmsLocationOption,
} from '../../services/tracking.service';
import SingleSelect, { SingleSelectOption } from '../../components/SingleSelect';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/**
 * CSX Terminals admin page.
 *
 * CSX is the one rail carrier where gwiTrace requires a terminal scope
 * on every import — its API is terminal-scoped (no nationwide endpoint).
 * The sweep can't submit a CSX shipment without knowing which terminal
 * to query.
 *
 * CMS doesn't carry the CSX terminal directly. We infer it from the
 * pickup leg's origin location (cms.legscurrent.origcode →
 * cms.locations.recno). This page is the admin surface for that
 * mapping: one row per (location_recno → CSX canonical terminal).
 *
 * The page mirrors AllowedCarriers (same patterns, look-and-feel) plus
 * a typeahead picker for finding CMS locations.
 */
export default function CsxTerminals() {
  const [rows, setRows] = useState<CsxTerminalMapping[]>([]);
  const [csxTerminals, setCsxTerminals] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-form state. The location picker uses a debounced typeahead;
  // selectedLocation captures what the admin actually picked from the
  // dropdown (vs. just typed) so we never submit a recno we didn't
  // confirm.
  const [selectedLocation, setSelectedLocation] = useState<CmsLocationOption | null>(null);
  const [newCsxTerminal, setNewCsxTerminal] = useState<string>('');
  const [adding, setAdding] = useState(false);

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<CsxTerminalMapping | null>(null);
  const [deleting, setDeleting] = useState(false);
  const {
    panelRef: deletePanelRef,
    panelStyle: deletePanelStyle,
    dragHandleProps: deleteDragHandleProps,
  } = useModalDrag(pendingDelete !== null);

  // Per-row save state for the inline terminal-change action. While a
  // recno is in this set, its cell shows "Saving…" next to the dropdown
  // and the trigger displays the in-flight value (optimistic).
  const [savingRecnos, setSavingRecnos] = useState<Set<number>>(new Set());
  // Optimistic value shown on the trigger while a save is in flight.
  // Cleared once the API responds (success or failure). Keyed by recno.
  const [optimisticTerminal, setOptimisticTerminal] = useState<Record<number, string>>({});
  // Brief green check after a successful save so the admin sees the
  // change registered (otherwise the spinner just disappears and they
  // can't tell if it stuck). Cleared after ~1.5s.
  const [recentlySaved, setRecentlySaved] = useState<Set<number>>(new Set());

  // SingleSelect option objects derived from the string list — same
  // shape gets reused on the Add form and per-row pickers.
  const terminalOptions: SingleSelectOption[] = useMemo(
    () => csxTerminals.map((name) => ({ value: name, label: name })),
    [csxTerminals],
  );

  const load = useCallback(async () => {
    try {
      setError(null);
      const [mappings, terminals] = await Promise.all([
        trackingService.listCsxTerminalMappings(),
        trackingService.listCsxTerminalNames().catch(() => [] as string[]),
      ]);
      setRows(mappings);
      setCsxTerminals(terminals);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!selectedLocation) {
      setError('Pick a location from the dropdown first.');
      return;
    }
    if (!newCsxTerminal) {
      setError('Select a CSX terminal.');
      return;
    }
    setAdding(true);
    try {
      setError(null);
      const created = await trackingService.createCsxTerminalMapping({
        location_recno: selectedLocation.recno,
        csx_terminal: newCsxTerminal,
      });
      setRows((prev) =>
        [...prev, created].sort(
          (a, b) =>
            a.csx_terminal.localeCompare(b.csx_terminal) ||
            a.location_recno - b.location_recno,
        ),
      );
      setSelectedLocation(null);
      setNewCsxTerminal('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add mapping');
    } finally {
      setAdding(false);
    }
  };

  const saveTerminal = async (row: CsxTerminalMapping, value: string) => {
    if (value === row.csx_terminal) return; // no-op
    const recno = row.location_recno;
    setError(null);
    setOptimisticTerminal((m) => ({ ...m, [recno]: value }));
    setSavingRecnos((s) => new Set(s).add(recno));
    try {
      const updated = await trackingService.updateCsxTerminalMapping(recno, value);
      setRows((prev) => prev.map((r) => (r.location_recno === recno ? updated : r)));
      // Flash a brief success indicator so the admin sees the save
      // landed rather than the spinner just disappearing.
      setRecentlySaved((s) => new Set(s).add(recno));
      setTimeout(() => {
        setRecentlySaved((s) => {
          const next = new Set(s);
          next.delete(recno);
          return next;
        });
      }, 1500);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setSavingRecnos((s) => {
        const next = new Set(s);
        next.delete(recno);
        return next;
      });
      setOptimisticTerminal((m) => {
        const next = { ...m };
        delete next[recno];
        return next;
      });
    }
  };

  const handleDelete = (row: CsxTerminalMapping) => setPendingDelete(row);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      setError(null);
      const recno = pendingDelete.location_recno;
      await trackingService.deleteCsxTerminalMapping(recno);
      setRows((prev) => prev.filter((r) => r.location_recno !== recno));
      setPendingDelete(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-body">CSX Terminals</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          CSX is the one rail carrier where gwiTrace requires a terminal scope on every
          import — its API is terminal-scoped, so each shipment must be fetched against a
          specific terminal. CMS doesn't carry that directly; we infer it from the
          pickup leg's origin location. Add one row per pickup location below, mapping it
          to a CSX canonical terminal. CSX orders whose pickup location has no mapping
          will be skipped at sweep time with a log warning.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Add form */}
      <div className="mb-6 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-medium text-body mb-3">Add a mapping</h2>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-label mb-1">
              Pickup location
            </label>
            <LocationPicker
              value={selectedLocation}
              onChange={setSelectedLocation}
              existingRecnos={new Set(rows.map((r) => r.location_recno))}
            />
          </div>
          <div className="w-56">
            <label className="block text-xs font-medium text-label mb-1">
              CSX terminal
            </label>
            <SingleSelect
              options={terminalOptions}
              value={newCsxTerminal}
              onChange={setNewCsxTerminal}
              placeholder={
                csxTerminals.length === 0
                  ? '— catalog unavailable —'
                  : '— select terminal —'
              }
              enableSearch
              searchPlaceholder="Search CSX terminals..."
              // Default size="md" — SingleSelect's md uses `py-1.5
              // text-sm` so the trigger lines up with the pickup-
              // location text input next to it (both are standard
              // form-row height).
              // Portal: keeps the dropdown's width tied to the trigger
              // (non-portal mode uses `w-max` which grows to the longest
              // option — undesirable for a fixed-width form column).
              usePortal
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !selectedLocation || !newCsxTerminal}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Mappings table */}
      {loading ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted italic">
          No mappings yet. Add one above so CSX shipments from that pickup location can
          be tracked.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  Pickup location
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  Recno
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  CSX terminal
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-muted uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {rows.map((row) => (
                <tr key={row.location_recno}>
                  <td className="px-4 py-2 text-sm text-body">
                    {row.location_lname || row.location_lcity || (
                      <span className="text-gray-400 italic">unknown</span>
                    )}
                    {row.location_state && (
                      <span className="text-muted ml-1">
                        ({row.location_state})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-muted">
                    {row.location_recno}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {/* Surface a saved value that's no longer in the
                        mirrored catalog (gwiTrace removed a terminal?
                        typo via direct SQL?) so admin can see + replace.
                        While a save is in flight we show the optimistic
                        value on the trigger so the admin sees their
                        click registered. */}
                    <div className="flex items-center gap-2">
                      <SingleSelect
                        options={
                          csxTerminals.includes(row.csx_terminal)
                            ? terminalOptions
                            : [
                                {
                                  value: row.csx_terminal,
                                  label: `${row.csx_terminal} (unknown)`,
                                },
                                ...terminalOptions,
                              ]
                        }
                        value={
                          optimisticTerminal[row.location_recno] ??
                          row.csx_terminal
                        }
                        onChange={(v) => saveTerminal(row, v)}
                        enableSearch
                        searchPlaceholder="Search CSX terminals..."
                        size="sm"
                        className="min-w-[12rem]"
                        disabled={savingRecnos.has(row.location_recno)}
                        // Portal: the parent table wraps in overflow-hidden,
                        // which clips a non-portal'd dropdown after a couple
                        // of options. Portal renders into <body>.
                        usePortal
                      />
                      {savingRecnos.has(row.location_recno) && (
                        <span className="flex items-center gap-1 text-xs text-muted italic">
                          <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Saving…
                        </span>
                      )}
                      {recentlySaved.has(row.location_recno) && !savingRecnos.has(row.location_recno) && (
                        <CheckCircleIcon
                          className="w-4 h-4 text-green-600 dark:text-green-400"
                          aria-label="Saved"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDelete(row)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                      title="Remove mapping"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation modal */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50"
          onClick={() => !deleting && setPendingDelete(null)}
        >
          <div
            ref={deletePanelRef}
            style={deletePanelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={
                <span className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                  Remove CSX mapping
                </span>
              }
              onClose={deleting ? undefined : () => setPendingDelete(null)}
              dragHandleProps={deleteDragHandleProps}
            />
            <div className="px-6 py-4">
              <p className="text-sm text-label">
                Remove the mapping{' '}
                <strong>
                  {pendingDelete.location_lname || pendingDelete.location_lcity || `recno ${pendingDelete.location_recno}`}
                </strong>{' '}
                → <strong>{pendingDelete.csx_terminal}</strong>?
              </p>
              <p className="mt-2 text-xs text-muted">
                CSX shipments picking up at this location will be skipped at the next
                sweep tick until another mapping covers it.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Typeahead search over cms.locations. Debounces input by ~200ms so we
 * don't fire a search-DB query on every keystroke. Filters out
 * locations already mapped (admin can't double-map a recno; the API
 * would 409 anyway, this just makes the UI cleaner).
 */
function LocationPicker({
  value,
  onChange,
  existingRecnos,
}: {
  value: CmsLocationOption | null;
  onChange: (v: CmsLocationOption | null) => void;
  existingRecnos: Set<number>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CmsLocationOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounced search. The backend ignores queries shorter than 2 chars
  // so the dropdown won't spam on the first keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await trackingService.searchCmsLocations(query.trim());
        setResults(found.filter((r) => !existingRecnos.has(r.recno)));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, existingRecnos]);

  // Close the dropdown on outside-click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const displayValue = useMemo(() => {
    if (value) {
      const name = value.lname || value.lcity || `recno ${value.recno}`;
      return `${name}${value.state ? ` (${value.state})` : ''} — recno ${value.recno}`;
    }
    return query;
  }, [value, query]);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={displayValue}
        onChange={(e) => {
          onChange(null);              // typing clears the locked-in selection
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search by name, city, or # (min 2 chars)"
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-1.5 text-sm"
      />
      {open && query.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-panel shadow-lg">
          {searching ? (
            <div className="px-3 py-2 text-xs text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted italic">
              No matches{existingRecnos.size > 0 ? ' (or already mapped)' : ''}.
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.recno}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(r);
                  setQuery('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 text-body"
              >
                <span>{r.lname || r.lcity || `recno ${r.recno}`}</span>
                {r.state && <span className="text-muted ml-1">({r.state})</span>}
                <span className="text-gray-400 dark:text-gray-500 ml-2 text-xs">
                  recno {r.recno}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
